import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useLists } from '../../context/ListsContext';
import hiveService from '../../services/hive';
import { Client } from '@hiveio/dhive';
import HIVE_RPC_NODES from '../../config/hiveNodes';
import './ArticleActions.css';
import VoteSlider from '../VoteSlider/VoteSlider';
import VotersModal from '../VotersModal/VotersModal';
import AddToListModal from '../AddToListModal/AddToListModal';
import BookmarkModal from '../BookmarkModal/BookmarkModal';

const client = new Client(HIVE_RPC_NODES);

const ArticleActions = ({ post, onScrollToComments, onReplyClick, showReply = false }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { isInFavourites, toggleFavourites, lists, loaded: listsLoaded } = useLists();
  const { addMutedUser, removeMutedUser, isUserMuted, showVoteSlider: globalShowVoteSlider, voteDefaultWeight, rewardDisplayMode, rewardDisplayCurrency } = useSettings();
  const isMountedRef = React.useRef(true);
  const lastClickTimeRef = React.useRef({});
  const [voteCount, setVoteCount] = useState(post.net_votes || 0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const [hasVoted, setHasVoted] = useState(false);
  const [userVoteWeight, setUserVoteWeight] = useState(null);
  const [isVoting, setIsVoting] = useState(false);
  const [showVoteSlider, setShowVoteSlider] = useState(false);
  const [voteWeight, setVoteWeight] = useState(voteDefaultWeight || 100);

  // reset both the stored vote weight and slider value when default changes
  useEffect(() => {
    setUserVoteWeight(null);
    setVoteWeight(voteDefaultWeight || 100);
  }, [voteDefaultWeight]);

  // sync slider value if the user vote weight changes independently
  useEffect(() => {
    if (userVoteWeight != null) {
      setVoteWeight(userVoteWeight);
    }
  }, [userVoteWeight]);
  const [isReblogging, setIsReblogging] = useState(false);
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [favouritesPending, setFavouritesPending] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showCopyNotice, setShowCopyNotice] = useState(false);
  const [copyNoticeMessage, setCopyNoticeMessage] = useState('Permlink copied');
  const menuRef = useRef(null);
  const copyNoticeTimeoutRef = useRef(null);
  const [showVotersModal, setShowVotersModal] = useState(false);
  const [activeVotesWithValue, setActiveVotesWithValue] = useState([]);
  // reward state (kept consistent with PostActions)
  const [pendingRewardHbd, setPendingRewardHbd] = useState(0);
  const [pendingRewardHive, setPendingRewardHive] = useState(0);
  const [totalPayoutHbd, setTotalPayoutHbd] = useState(0);
  const [totalPayoutHive, setTotalPayoutHive] = useState(0);
  // cashoutTime tracked for potential future use (upcoming payout countdown)
  // eslint-disable-next-line no-unused-vars
  const [cashoutTime, setCashoutTime] = useState(null);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const parentAuthor = String(post?.parentAuthor || post?.parent_author || '').trim();
  const depthValue = Number(post?.depth);
  const isArticle = Number.isFinite(depthValue) ? depthValue === 0 : !parentAuthor;

  const metadata = useMemo(() => {
    const rawMetadata = post?.json_metadata ?? post?.metadata ?? post?.jsonMetadata ?? null;
    if (!rawMetadata) return null;
    if (typeof rawMetadata === 'string') {
      try {
        return JSON.parse(rawMetadata);
      } catch (error) {
        return null;
      }
    }
    return typeof rawMetadata === 'object' ? rawMetadata : null;
  }, [post]);

  const txId = useMemo(() => {
    if (!post) return null;
    if (post.tx_id) return post.tx_id;
    if (post.txId) return post.txId;
    return metadata?.tx_id || metadata?.txId || null;
  }, [post, metadata]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
      if (copyNoticeTimeoutRef.current) {
        clearTimeout(copyNoticeTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyLink = useCallback(async () => {
    const postUrl = `@${post.author}/${post.permlink}`;
    let copied = false;

    try {
      await navigator.clipboard.writeText(postUrl);
      copied = true;
    } catch (error) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = postUrl;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        copied = document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch (fallbackError) {
        copied = false;
      }
    }

    setShowMenu(false);
    setCopyNoticeMessage(copied ? 'Permlink copied' : 'Unable to copy link');
    setShowCopyNotice(true);

    if (copyNoticeTimeoutRef.current) {
      clearTimeout(copyNoticeTimeoutRef.current);
    }

    copyNoticeTimeoutRef.current = setTimeout(() => {
      setShowCopyNotice(false);
      copyNoticeTimeoutRef.current = null;
    }, 1600);
  }, [post.author, post.permlink]);

  useEffect(() => {
    const parseAsset = (asset) => {
      if (!asset || typeof asset !== 'string') return { amount: null, symbol: null };
      const [amount, symbol] = asset.split(' ');
      return { amount: amount ? parseFloat(amount) : null, symbol: symbol || null };
    };

    const getHivePerHbd = (price) => {
      const base = parseAsset(price?.base);
      const quote = parseAsset(price?.quote);
      if (!base.amount || !quote.amount || !base.symbol || !quote.symbol) return null;
      if (base.symbol === 'HBD' && quote.symbol === 'HIVE') return quote.amount / base.amount;
      if (base.symbol === 'HIVE' && quote.symbol === 'HBD') return base.amount / quote.amount;
      return null;
    };

    const fetchVoteData = async () => {
      setRewardsLoading(true);
      try {
        let rewardHive = 0;
        let pendingHBD = 0;

        const [content, activeVotes, dynamicProps, medianHistoryPrice] = await Promise.all([
          client.database.call('get_content', [post.author, post.permlink]),
          client.database.call('get_active_votes', [post.author, post.permlink]),
          client.database.call('get_dynamic_global_properties', []),
          client.call('condenser_api', 'get_current_median_history_price', [])
        ]);

        const upvotes = activeVotes.filter(v => v.percent > 0).length;
        setVoteCount(upvotes);

        if (content && content.pending_payout_value) {
          pendingHBD = parseFloat(content.pending_payout_value) || 0;
          const dynamicRate = getHivePerHbd(dynamicProps?.hbd_exchange_rate);
          const medianRate = getHivePerHbd(medianHistoryPrice);
          const hbdExchangeRate = dynamicRate ?? medianRate ?? 1;
          rewardHive = pendingHBD * hbdExchangeRate;

          setPendingRewardHbd(pendingHBD);
          setPendingRewardHive(rewardHive);

          const totalPayoutValue = parseFloat(content.total_payout_value) || 0;
          const curatorPayoutValue = parseFloat(content.curator_payout_value) || 0;
          const totalPayoutHBD = totalPayoutValue + curatorPayoutValue;
          const totalPayoutHIVE = totalPayoutHBD * hbdExchangeRate;
          setTotalPayoutHbd(totalPayoutHBD);
          setTotalPayoutHive(totalPayoutHIVE);
        }

        // Calculate individual vote values (for voters modal)
        if (activeVotes && content && (rewardHive > 0 || pendingHBD > 0)) {
          const netRshares = parseFloat(content.net_rshares) || 0;
          const votesWithValue = activeVotes.map(vote => {
            const rshares = parseFloat(vote.rshares) || 0;
            const share = netRshares > 0 ? (rshares / netRshares) : 0;
            return {
              ...vote,
              reward_hive: share > 0 ? share * rewardHive : 0,
              reward_hbd: share > 0 ? share * pendingHBD : 0
            };
          });
          setActiveVotesWithValue(votesWithValue);
        }

        if (content && content.cashout_time) {
          setCashoutTime(new Date(content.cashout_time + 'Z'));
        }

        if (isAuthenticated && user?.username) {
          const userVote = activeVotes.find(v => v.voter === user.username);
          setHasVoted(!!userVote && userVote.percent > 0);
          setUserVoteWeight(userVote && userVote.percent ? Math.max(1, Math.round(userVote.percent / 100)) : null);
        }
      } catch (error) {
        console.error('Error fetching vote data:', error);
      } finally {
        setRewardsLoading(false);
      }
    };

    fetchVoteData();
  }, [post.author, post.permlink, isAuthenticated, user?.username, rewardDisplayMode, rewardDisplayCurrency]);

  const handleVote = useCallback(async (isUpvote = true, explicitWeight = null) => {
    if (!isMountedRef.current) return;
    if (!isAuthenticated) {
      alert('Please login to vote');
      return;
    }

    setIsVoting(true);
    try {
      let percent = explicitWeight !== null ? explicitWeight : voteWeight;
      if (typeof percent !== 'number' || isNaN(percent)) {
        percent = voteDefaultWeight || 100;
      }
      const weight = (isUpvote ? 1 : -1) * (percent * 100);
      await hiveService.vote(user.username, post.author, post.permlink, weight);
      if (!isMountedRef.current) return;
      setShowVoteSlider(false);
      setHasVoted(isUpvote ? true : false);
      setVoteCount(prev => prev + (isUpvote ? 1 : -1));
      alert('Vote submitted successfully! Weight: ' + percent + '%');
    } catch (error) {
      console.error('Vote error:', error);
      alert('Failed to vote: ' + error.message);
    } finally {
      if (isMountedRef.current) setIsVoting(false);
    }
  }, [isAuthenticated, voteWeight, voteDefaultWeight, user?.username, post?.author, post?.permlink]);

  const handleVoteClick = useCallback(() => {
    const now = Date.now();
    if (lastClickTimeRef.current['vote'] && now - lastClickTimeRef.current['vote'] < 300) return;
    lastClickTimeRef.current['vote'] = now;

    if (!isAuthenticated) {
      alert('Please login to vote');
      return;
    }

    if (!globalShowVoteSlider) {
      const wt = voteDefaultWeight || 100;
      handleVote(true, wt);
      return;
    }

    setVoteWeight(userVoteWeight != null ? userVoteWeight : (voteDefaultWeight || 100));
    setShowVoteSlider(prev => !prev);
  }, [isAuthenticated, globalShowVoteSlider, voteDefaultWeight, userVoteWeight, handleVote]);

  const handleReblog = async () => {
    if (!isAuthenticated) {
      alert('Please login to reblog');
      return;
    }

    setIsReblogging(true);
    try {
      await hiveService.reblog(user.username, post.author, post.permlink);
      alert('Post reblogged successfully!');
    } catch (error) {
      console.error('Reblog error:', error);
      alert('Failed to reblog: ' + error.message);
    } finally {
      setIsReblogging(false);
    }
  };

  return (
    <>
      <div className="article-actions-bar">
        {onScrollToComments && (
          <button 
            className="article-action-btn comment-btn"
            onClick={onScrollToComments}
            title="Jump to comments"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span>{post.children || 0}</span>
          </button>
        )}

        <div className="vote-container">
          <button 
            className={`article-action-btn vote-btn ${hasVoted ? 'voted' : ''}`}
            onClick={handleVoteClick}
            disabled={isVoting}
            title={hasVoted ? 'You have already voted' : 'Upvote'}
          >
            <svg viewBox="0 0 24 24" fill={hasVoted ? "var(--accent-primary)" : "none"} stroke={hasVoted ? "var(--accent-primary)" : "currentColor"} strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            <span>{voteCount}</span>
          </button>

          {showVoteSlider && (
            <VoteSlider
              voteWeight={voteWeight}
              setVoteWeight={setVoteWeight}
              onCancel={() => setShowVoteSlider(false)}
              onVote={(isUp, explicit) => handleVote(isUp, explicit)}
              isVoting={isVoting}
              showDownvote={false}
              userHasVoted={hasVoted}
              userVoteWeight={userVoteWeight}
            />
          )}
        </div>

        {!(post?.depth > 0 || post?.parent_author) && (
          <button 
            className="article-action-btn reblog-btn"
            onClick={handleReblog}
            disabled={isReblogging}
            title="Reflux (Reblog)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="17 1 21 5 17 9" />
              <path d="M3 11V9a4 4 0 0 1 4-4h14" />
              <polyline points="7 23 3 19 7 15" />
              <path d="M21 13v2a4 4 0 0 1-4 4H3" />
            </svg>
            <span>Reflux</span>
          </button>
        )}

        {isAuthenticated && (
          <button
            className="article-action-btn add-to-list-btn"
            onClick={() => setShowAddToListModal(true)}
            title={`Add @${post.author} to list`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 6h13" />
              <path d="M8 12h13" />
              <path d="M8 18h13" />
              <path d="M3 12h.01" />
            </svg>
            <span>Add author to list</span>
          </button>
        )}

        <button 
          className={`article-action-btn reward-btn ${rewardsLoading ? 'loading' : ''}`}
          onClick={() => setShowVotersModal(true)}
          title={(() => {
            const rewardBase = rewardDisplayCurrency === 'hbd' ? pendingRewardHbd : pendingRewardHive;
            if (rewardBase > 0) return '';
            const totalPaidOut = rewardDisplayCurrency === 'hbd' ? totalPayoutHbd : totalPayoutHive;
            if (totalPaidOut > 0) return `Paid out: ${(rewardDisplayMode === 'author_only' ? (totalPaidOut * 0.5) : totalPaidOut).toFixed(3)} ${rewardDisplayCurrency === 'hbd' ? 'HBD' : 'HIVE'}`;
            return 'No pending rewards';
          })()}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="8" r="7" />
            <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
          </svg>
          {rewardsLoading ? (
            <span className="loading-spinner"></span>
          ) : (
            (() => {
              const rewardBase = rewardDisplayCurrency === 'hbd' ? pendingRewardHbd : pendingRewardHive;
              const base = rewardBase > 0 ? rewardBase : 0;
              const display = rewardDisplayMode === 'author_only' ? base * 0.5 : base;
              const unit = rewardDisplayCurrency === 'hbd' ? 'HBD' : 'HIVE';
              return base > 0 ? <span>{display.toFixed(3)} {unit}</span> : null;
            })()
          )}
        </button>

        {showReply && onReplyClick && (
          <button 
            className="article-action-btn reply-btn"
            onClick={onReplyClick}
            title={isArticle ? 'Reply to article' : 'Reply to comment'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 10h10a8 8 0 0 1 8 8v2M3 10l6 6m-6-6 6-6" />
            </svg>
            <span>Reply</span>
          </button>
        )}

        <div className="action-dropdown-wrapper" ref={menuRef}>
          <button
            className="article-action-btn dropdown-toggle"
            onClick={() => setShowMenu(prev => !prev)}
            title="More actions"
            aria-haspopup="true"
            aria-expanded={showMenu}
          >
            ⋯
          </button>
          {showMenu && (
            <div className="action-dropdown-menu">
              {user?.username?.toLowerCase() === post?.author?.toLowerCase() &&
                post?.created &&
                (Date.now() - new Date(post.created).getTime()) < 7 * 24 * 60 * 60 * 1000 && (
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowMenu(false);
                    navigate(`/create-article?edit=true&author=${encodeURIComponent(post.author)}&permlink=${encodeURIComponent(post.permlink)}`);
                  }}
                >
                  Edit
                </button>
              )}
              {user?.username && user?.username?.toLowerCase() !== post?.author?.toLowerCase() && listsLoaded && (
                <button
                  className="dropdown-item"
                  disabled={favouritesPending}
                  onClick={async () => {
                    setFavouritesPending(true);
                    try { await toggleFavourites(post.author); } catch (e) {}
                    setFavouritesPending(false);
                  }}
                >
                  {isInFavourites(post.author) ? '★ Remove from favourites' : '☆ Add to favourites'}
                </button>
              )}
              {user?.username && user?.username?.toLowerCase() !== post?.author?.toLowerCase() && listsLoaded && lists.length > 0 && (
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowMenu(false);
                    setShowAddToListModal(true);
                  }}
                >
                  ☰ Add to list
                </button>
              )}
              {user?.username && (
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowMenu(false);
                    setShowBookmarkModal(true);
                  }}
                >
                  🔖 Bookmark
                </button>
              )}
              {user?.username && user?.username?.toLowerCase() !== post?.author?.toLowerCase() && (
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowMenu(false);
                    if (isUserMuted(post.author)) {
                      if (window.confirm(`Unmute @${post.author}?`)) {
                        removeMutedUser(post.author);
                      }
                    } else {
                      if (window.confirm(`Are you sure you want to mute user\n@${post.author}?`)) {
                        addMutedUser(post.author);
                      }
                    }
                  }}
                >
                  {isUserMuted(post.author) ? 'Unmute user' : 'Mute user'}
                </button>
              )}
              <Link
                to={`/user/${post.author}`}
                className="dropdown-item"
                onClick={() => setShowMenu(false)}
              >
                View profile
              </Link>
              <button className="copy-link-btn dropdown-item" onClick={handleCopyLink}>
                Copy link
              </button>
              {txId && (
                <a
                  href={`https://hivexplorer.com/tx/${txId}`}
                  className="dropdown-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open on Hivexplorer"
                  onClick={() => setShowMenu(false)}
                >
                  Show Tx ID
                </a>
              )}
            </div>
          )}
          {showCopyNotice && (
            <div className="copy-link-notice" role="status" aria-live="polite">
              {copyNoticeMessage}
            </div>
          )}
        </div>
      </div>

      {showVotersModal && (
        <VotersModal
          author={post.author}
          permlink={post.permlink}
          votesWithRewards={activeVotesWithValue}
          onClose={() => setShowVotersModal(false)}
        />
      )}

      {showAddToListModal && (
        <AddToListModal
          targetUsername={post.author}
          onClose={() => setShowAddToListModal(false)}
        />
      )}

      {showBookmarkModal && (
        <BookmarkModal
          author={post.author}
          permlink={post.permlink}
          onClose={() => setShowBookmarkModal(false)}
        />
      )}

    </>
  );
};

export default ArticleActions;
